import { useEffect, useState } from 'react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import { formatCurrency } from '../../data/mockData';
import { formatStock } from '../../utils/soldBy';
import {
  getProduct,
  mergeProducts as mergeInventoryProducts,
  normalizeProduct,
} from '../../api/inventory';
import {
  SOLD_BY_MISMATCH_COPY,
  buildMergeBody,
  collectBarcodeAliases,
  isMergeSuccess,
  isMergedProduct,
  mergeErrorCopy,
  previewOnHand,
  soldAsLabel,
  soldByMismatch,
  tombstoneTarget,
} from '../../utils/productMerge';
import { SELLING_PRICE_MRP_MESSAGE, isSellingBelowMrp } from '../../utils/posMoney';
import styles from './ProductMerge.module.css';

const COMPARE_ROWS = [
  { key: 'image_keys', label: 'Photo' },
  { key: 'name', label: 'Name' },
  { key: 'sku', label: 'SKU' },
  { key: 'barcode', label: 'Barcode' },
  { key: 'brand', label: 'Brand' },
  { key: 'category', label: 'Category' },
  { key: 'cost_price', label: 'Cost' },
  { key: 'selling_price', label: 'Sell' },
  { key: 'mrp', label: 'MRP' },
  { key: 'hsn', label: 'HSN' },
  { key: 'gst_rate', label: 'GST' },
  { key: 'status', label: 'Status' },
  { key: 'stock', label: 'On hand', readOnly: true },
  { key: 'sold_by', label: 'Sold as', readOnly: true },
];

function statusLabel(status) {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'INACTIVE') return 'Inactive';
  return status || '—';
}

function categoryLabel(categories, value) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '—';
  const match = (categories || []).find((category) => (
    String(category.id || '').trim().toLowerCase() === key
    || String(category.name || '').trim().toLowerCase() === key
  ));
  return match?.name || value;
}

function fieldText(item, key, categories) {
  if (!item) return '—';
  if (key === 'name') return item.name || '—';
  if (key === 'sku') return item.sku || '—';
  if (key === 'barcode') return item.barcode || '—';
  if (key === 'brand') return item.brand || '—';
  if (key === 'category') return categoryLabel(categories, item.category);
  if (key === 'cost_price') return formatCurrency(item.costPrice);
  if (key === 'selling_price') return formatCurrency(item.price);
  if (key === 'mrp') return formatCurrency(item.mrp);
  if (key === 'hsn') return item.hsnCode || '—';
  if (key === 'gst_rate') return item.gstRate === 0 || item.gstRate ? `${item.gstRate}%` : '—';
  if (key === 'status') return statusLabel(item.status);
  if (key === 'stock') return formatStock(item.stock, item);
  if (key === 'sold_by') return soldAsLabel(item);
  return '—';
}

function canWinStatus(item) {
  const status = String(item?.status || '').toUpperCase();
  return status === 'ACTIVE' || status === 'INACTIVE';
}

export default function ProductMerge({
  open,
  onClose,
  selectedIds,
  token,
  categories,
  onMerged,
  onForbidden,
  onRefresh,
  onOpenSurvivor,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [survivorId, setSurvivorId] = useState('');
  const [winners, setWinners] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const selectionKey = (selectedIds || []).join('|');

  useEffect(() => {
    if (!open) return undefined;
    const ids = selectionKey ? selectionKey.split('|') : [];
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setError('');
    setWinners({});
    setSurvivorId(ids[0] || '');
    setItems([]);
    (async () => {
      try {
        const rows = await Promise.all(ids.map((id) => getProduct(id, token)));
        if (cancelled) return;
        setItems(rows);
      } catch (err) {
        if (cancelled) return;
        setLoadError(mergeErrorCopy(err).message || 'Could not load these products.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectionKey, token]);

  const mismatch = soldByMismatch(items);
  const tombstone = items.find((item) => isMergedProduct(item));
  const distinct = new Set(items.map((item) => item.id)).size === items.length;
  const winningSell = items.find((item) => item.id === (winners.selling_price || survivorId));
  const winningMrp = items.find((item) => item.id === (winners.mrp || survivorId));
  const pricesOk = items.length === 0 || isSellingBelowMrp(winningSell?.price, winningMrp?.mrp);
  const ready = items.length >= 2
    && items.length <= 6
    && distinct
    && !mismatch
    && pricesOk
    && !loadError
    && !loading
    && !tombstone;
  const combined = previewOnHand(items);

  const chooseSurvivor = (id) => {
    setSurvivorId(id);
    setWinners({});
  };

  const handleConfirm = async () => {
    if (!ready || submitting || !survivorId) return;
    setSubmitting(true);
    setError('');
    const donorIds = items.map((item) => item.id).filter((id) => id !== survivorId);
    const body = buildMergeBody({
      survivorId,
      donorIds,
      items,
      winners,
    });
    try {
      let response;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          response = await mergeInventoryProducts({
            survivor_id: body.survivor_id,
            donor_ids: body.donor_ids,
            fields: body.fields,
          }, token);
          break;
        } catch (err) {
          const copy = mergeErrorCopy(err);
          if (copy.retry && attempt === 0) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 1500));
            continue;
          }
          throw err;
        }
      }
      if (!isMergeSuccess(response)) {
        setError('Could not merge these products.');
        return;
      }
      let survivor = normalizeProduct(response.item || {});
      try {
        survivor = await getProduct(survivorId, token);
      } catch {
        // The merge response item is enough to open the editor.
      }
      const keptBarcode = survivor.barcode;
      onMerged?.({
        survivor,
        donorIds: response.donor_ids || donorIds,
        aliases: collectBarcodeAliases(keptBarcode, [
          ...(response.aliases || []),
          ...items.map((item) => item.barcode),
        ]),
      });
    } catch (err) {
      const copy = mergeErrorCopy(err);
      if (copy.hide) onForbidden?.();
      if (copy.refresh) onRefresh?.();
      const into = tombstoneTarget(err);
      if (into) {
        onOpenSurvivor?.(into);
        return;
      }
      setError(copy.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Merge products"
      subtitle="Choose which product stays, then pick the details to keep."
      width={1040}
      footer={(
        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={!ready || submitting}>
            {submitting ? 'Merging…' : 'Merge'}
          </Button>
        </div>
      )}
    >
      <ul className={styles.notes}>
        <li>Old bills and invoices keep the name they were printed with.</li>
        <li>Stock and product insight move to the product you keep.</li>
        <li>Every barcode here will open the product you keep.</li>
        <li>Cancel or finish active subscriptions on a duplicate before merging.</li>
      </ul>

      {loading && <p className={styles.status}>Loading products…</p>}
      {loadError && <p className={styles.error} role="alert">{loadError}</p>}
      {error && <p className={styles.error} role="alert">{error}</p>}
      {mismatch && <p className={styles.error} role="alert">{SOLD_BY_MISMATCH_COPY}</p>}
      {items.length > 0 && !pricesOk && (
        <p className={styles.error} role="alert">{SELLING_PRICE_MRP_MESSAGE}</p>
      )}
      {tombstone && (
        <p className={styles.error} role="alert">
          This product was already merged.
          {tombstone.mergedInto && (
            <>
              {' '}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => onOpenSurvivor?.(tombstone.mergedInto)}
              >
                Open the product that stayed
              </button>
            </>
          )}
        </p>
      )}

      {items.length > 0 && (
        <>
          <p className={styles.combine}>
            Will combine to ≈ {formatStock(combined, items[0])}
          </p>
          <div className={styles.scroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.fieldCol}>Field</th>
                  {items.map((item) => (
                    <th key={item.id}>
                      <label className={styles.survivorPick}>
                        <input
                          type="radio"
                          name="merge-survivor"
                          checked={survivorId === item.id}
                          onChange={() => chooseSurvivor(item.id)}
                          disabled={submitting}
                        />
                        <span>{survivorId === item.id ? 'Stays' : 'Keep this one'}</span>
                      </label>
                      <span className={styles.colName}>{item.name || item.id}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    {items.map((item) => {
                      const picked = (winners[row.key] || survivorId) === item.id;
                      const statusBlocked = row.key === 'status' && !canWinStatus(item);
                      return (
                        <td key={item.id} className={picked && !row.readOnly ? styles.picked : undefined}>
                          {row.key === 'image_keys' && item.images?.[0]?.url ? (
                            <img src={item.images[0].url} alt="" className={styles.photo} />
                          ) : (
                            <span>{fieldText(item, row.key, categories)}</span>
                          )}
                          {!row.readOnly && (
                            <label className={styles.useThis}>
                              <input
                                type="radio"
                                name={`merge-field-${row.key}`}
                                checked={picked}
                                disabled={submitting || statusBlocked}
                                onChange={() => setWinners((prev) => ({ ...prev, [row.key]: item.id }))}
                              />
                              Use this
                            </label>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Drawer>
  );
}
